import { selectUserInfo } from "features/user/userSlice";
import { Grid, InputAdornment, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { UserModuleProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as User } from "../../assets/SideBarIcons/users.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { GetUserByIdResponse } from "subModule/src/services/user/interface";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import UserTabs from "./UserTabs";
import { useGetRoleDropdownService } from "subModule/src/services/role/useRole";
import CommonCheckbox from "common/CheckBox/Checkbox";
import language from "common/Others/Language";
import currency from "common/Others/Currency";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
// import ReplayIcon from '@mui/icons-material/Replay';
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import {
  useAssignUserService,
  useGetStationDropdownService,
} from "subModule/src/services/station/useStation";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}

const EXPERTISE_OPTION_KEYS = [
  { value: 1, labelKey: "MECHANICAL" },
  { value: 2, labelKey: "ELECTRICAL" },
  { value: 3, labelKey: "DIAGNOSTICS" },
  { value: 4, labelKey: "MAINTENANCE" },
  { value: 5, labelKey: "BODY_AND_PAINT" },
  { value: 6, labelKey: "AC_AND_COOLING" },
  { value: 7, labelKey: "TYRE_AND_WHEEL" },
  { value: 8, labelKey: "ADVANCED_SYSTEM" },
  { value: 9, labelKey: "HELPER" },
] as const;

const LOGIN_ACCESS_USER_TYPES = [
  -1, 100, 110, 120, 130, 140, 150, 190, 200, 210, 230, 240,
] as const;

const OWNER_ONLY_USER_TYPES = [230, 240] as const;

const getUserTypeValue = (type: any) => Number(type?.value ?? type);

const shouldSetLoginAccess = (type: any) =>
  LOGIN_ACCESS_USER_TYPES.includes(
    getUserTypeValue(type) as (typeof LOGIN_ACCESS_USER_TYPES)[number],
  );

export default function UsersModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, dirtyFields, isDirty },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({ defaultValues: {} });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedUserFullNamePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phUserFullNameAr") : t("phUserFullNameEn");
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "general",
    moduleLabelKey: "users",
    moduleHref: "/users",
    entityFallbackLabelKey: "name",
    isEdit: !!params?.id,
  });
  const [phoneLabel, setPhoneLabel] = useState<string>("phoneNumber");
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: rolesData, refetch: rolesRefetch } = useGetRoleDropdownService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm,
  });
  const { data: stationsData, refetch: stationsRefetch } =
    useGetStationDropdownService({
      pageNumber: pageNo,
      size: 50,
      name: dropdownSearchTerm,
    });
  const roles = (rolesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const stations = (stationsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; name: string }) => ({
      value: item.id,
      label: item.name + " (" + item.code + ")",
    }),
  );
  const expertiseOptions = EXPERTISE_OPTION_KEYS.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));
  const getExpertiseOption = (value?: number | number[] | null) => {
    const expertiseValue = Array.isArray(value) ? value[0] : value;
    if (!expertiseValue) return null;
    const match = EXPERTISE_OPTION_KEYS.find(
      (option) => Number(option.value) === Number(expertiseValue),
    );
    return match ? { value: match.value, label: t(match.labelKey) } : null;
  };
  const mapExpertisePayload = (expertise: any) => {
    const value = expertise?.value ?? expertise;
    if (value == null || value === "") return undefined;
    return [Number(value)];
  };
  const isTechnicianType = (type: any) => Number(type?.value ?? type) === 150;
  const loggedInUserType = loggedInUserData.type;
  const userTypesData = [
    { value: 100, label: t("Reseller") },
    { value: 110, label: t("Maintenance Head") },
    { value: 120, label: t("Admin") },
    { value: 130, label: t("Station Manager") },
    { value: 140, label: t("Service Manager") },
    { value: 150, label: t("Technician") },
    { value: 200, label: t("Supervisor") },
    { value: 210, label: t("Inventory Manager") },
    { value: 220, label: t("Account Manager") },
    { value: 230, label: t("Finance Controller") },
    { value: 240, label: t("Technical Support") },
  ];
  const isOwnerOnlyUserType = (value: number) =>
    OWNER_ONLY_USER_TYPES.includes(
      value as (typeof OWNER_ONLY_USER_TYPES)[number],
    );
  const userTypesOption =
    loggedInUserType === -1
      ? userTypesData.filter(
          (option) =>
            option.value === 100 ||
            option.value === 110 ||
            isOwnerOnlyUserType(option.value),
        )
      : loggedInUserType === 100
        ? userTypesData.filter((option) => option.value === 110)
        : loggedInUserType === 110
          ? userTypesData.filter(
              (option) =>
                option.value > loggedInUserType &&
                !isOwnerOnlyUserType(option.value),
            )
          : loggedInUserType === 120
            ? userTypesData.filter((option) =>
                [130, 140, 150].includes(option.value),
              )
            : loggedInUserType === 130
              ? userTypesData.filter((option) =>
                  [140, 150].includes(option.value),
                )
              : loggedInUserType === 140
                ? userTypesData.filter((option) => [150].includes(option.value))
                : [];
  useEffect(() => {
    rolesRefetch();
    stationsRefetch();
  }, []);
  useEffect(() => {
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params, stationsData, rolesData]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    UserModuleProfileService.getUserByID(+(params as any)?.id)
      .then((res: GetUserByIdResponse) => {
        if (res.success) {
          setPhoneLabel(
            res?.data?.type === 110 ||
              res?.data?.type === 100 ||
              res?.data?.type === 230 ||
              res?.data?.type === 240
              ? "phoneNumber"
              : "mobileNumber",
          );
          reset({
            ...(res?.data || {}),
            roleId: { value: res.data?.roleId, label: res?.data?.roleTitle },
            expertise: getExpertiseOption((res.data as any)?.expertise),
            stationIds:
              Array.isArray((res?.data as any)?.stations) &&
              (res?.data as any)?.stations?.[0]
                ? {
                    value: (res?.data as any)?.stations?.[0]?.stationId,
                    label: (res?.data as any)?.stations?.[0]?.stationName,
                  }
                : null,
            business: {
              ...res?.data?.business,
              currency: res.data.business?.currency
                ? {
                    value: res.data.business?.currency || "",
                    label:
                      currency.find(
                        (v) => v.value === res?.data.business?.currency,
                      )?.title ||
                      res?.data.business?.currency ||
                      "",
                  }
                : null,
              primaryLanguage: res.data.business?.primaryLanguage
                ? {
                    value: res.data.business?.primaryLanguage || "",
                    label: res?.data.business?.primaryLanguage || "",
                  }
                : null,
              secondaryLanguage: res?.data.business?.secondaryLanguage
                ? {
                    value: res?.data.business?.secondaryLanguage || "",
                    label: res?.data.business?.secondaryLanguage || "",
                  }
                : null,
            },
          });
          captureLabel(res?.data?.name);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    // if(data.type !== 110) delete data.taxNumber;
    // if(data.type !== 150) delete data.employeeId;
    if (params?.id) {
      let payload: any = {};
      if (data.type !== 110 && data.type !== 100) {
        payload = {
          ...data,
          roleId: data?.roleId?.value || 0,
          email: data.username,
          expertise: isTechnicianType(data.type)
            ? mapExpertisePayload(data?.expertise)
            : undefined,
        };
        delete payload?.business;
      } else {
        payload = {
          ...data,
          roleId: data?.roleId?.value || 0,
          email: data.username,
          business: {
            ...data.business,
            currency: data?.business?.currency?.value
              ? data.business.currency.value
              : "",
            primaryLanguage: data?.business?.primaryLanguage?.value
              ? data.business.primaryLanguage.value
              : "",
            secondaryLanguage: data?.business?.secondaryLanguage?.value
              ? data.business.secondaryLanguage.value
              : "",
          },
        };
      }
      delete payload.stations;
      if (data.type !== 110 && data.type !== 100) {
        payload.stationIds = data?.stationIds?.value
          ? [data.stationIds.value]
          : [];
      } else {
        delete payload.stationIds;
      }
      if (data.type !== 150) {
        delete payload.expertise;
      }
      if (shouldSetLoginAccess(data.type)) {
        payload.loginAccess = true;
      }
      UserModuleProfileService.updateUser(+params?.id, payload)
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.name);
            getByID();
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("user") + " " + t("updated") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      let station = 0;
      delete data.showConfirmPassword;
      delete data.showPassword;
      delete data.confirmPassword;
      let payload: any = {};
      if (data.type.value !== 110 && data.type.value !== 100) {
        payload = {
          ...data,
          roleId: data?.roleId?.value || 0,
          type: data.type.value,
          email: data.username,
          active: true,
          stationIds: [data?.stationIds?.value],
          expertise: isTechnicianType(data.type)
            ? mapExpertisePayload(data?.expertise)
            : undefined,
        };
        delete payload?.business;
      } else {
        payload = {
          ...data,
          roleId: data?.roleId?.value || 0,
          type: data.type.value,
          email: data.username,
          active: true,
          business: {
            ...data?.business,
            currency: data?.business?.currency?.value
              ? data.business.currency.value
              : "",
            primaryLanguage: data?.business?.primaryLanguage?.value
              ? data.business.primaryLanguage.value
              : "",
            secondaryLanguage: data?.business?.secondaryLanguage?.value
              ? data.business.secondaryLanguage.value
              : "",
          },
        };
      }
      if (data?.stationId?.value) station = data.stationId.value;
      delete payload?.stationId;
      if (!isTechnicianType(data.type)) {
        delete payload.expertise;
      }
      if (shouldSetLoginAccess(data.type)) {
        payload.loginAccess = true;
      }
      UserModuleProfileService.createUser(payload)
        .then((response: any) => {
          if (response.success) {
            if (station) {
              onAssignUser({ stationId: station, userId: response?.data?.id });
            } else {
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("user") + " " + t("created") + " " + t("successfully")
                  }
                />,
                { variant: "success" },
              );
              navigate("/users");
            }
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const { onAssignUser } = useAssignUserService({
    onSuccess: async (msg) => {
      const response: any = {};
      Object.keys(msg).forEach((key, index) => {
        response[key] = Object.values(msg)[index];
      });
      if (response.success) {
        enqueueSnackbar(
          <AlertMessages
            statusCode={response.status}
            text={t("user") + " " + t("created") + " " + t("successfully")}
          />,
          { variant: "success" },
        );
        navigate("/users");
      } else {
        setIsBtnEnabled(true);
        enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
          variant: "error",
        });
      }
    },
    onError: (err: any) => {
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      if (err instanceof Error) {
        console.error(err.stack);
      }
      setIsBtnEnabled(true);
    },
  });
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "roles")
        rolesRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "stations")
        stationsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate("/users")}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("editUser") : t("addUser")}
        icon={<User height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_USER_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      {loading ? (
        <Loader />
      ) : (
        <div className={`${params?.id ? "flex" : ""}`}>
          {params?.id ? <UserTabs params={params} selectedIndex={0} /> : null}
          <SimpleModal
            visable={true}
            title={params?.id ? t("editUser") : t("addUser")}
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <Grid
                container
                component="form"
                id="form"
                spacing={3}
                onSubmit={handleSubmit(onSubmit)}
              >
                {params?.id ? null : (
                  <Grid item container xs={12} spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="type"
                        control={control}
                        defaultValue={null}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("userType") + " *"}
                            {...field}
                            options={userTypesOption}
                            isSearchable
                            onChange={(selectedOption: any) => {
                              field.onChange(selectedOption);
                              setPhoneLabel(
                                selectedOption?.value === 110 ||
                                  selectedOption?.value === 100 ||
                                  selectedOption?.value === 230 ||
                                  selectedOption?.value === 240
                                  ? "phoneNumber"
                                  : "mobileNumber",
                              );
                              if (selectedOption?.value !== 130)
                                setValue("stationId", null);
                              if (selectedOption?.value !== 110)
                                setValue("taxNumber", null);
                              if (selectedOption?.value !== 150) {
                                setValue("employeeId", null);
                                setValue("expertise", null);
                              }
                            }}
                            error={errors.type?.message as string}
                          />
                        )}
                      />
                    </Grid>
                    {/* {watch("type")?.value === 130 ? ( 
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="stationId"
                        control={control}
                        defaultValue={null}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("station") + " *"}
                            {...field}
                            options={stations}
                            isClearable
                            isSearchable
                            onChange={(selectedOption: any) => {
                              field.onChange(selectedOption);
                            }}
                            error={errors.stationId?.message as string}
                            isLoading={searchLoading === "stations"}
                            onInputChange={(
                              search: string,
                              { action }: { action: string },
                            ) => {
                              if (
                                action === "input-change" ||
                                (action !== "set-value" &&
                                  action !== "menu-close" &&
                                  action !== "input-blur")
                              )
                                handleDropdownActions(
                                  "stations",
                                  "searchIt",
                                  search,
                                );
                              else if (
                                action === "input-blur" ||
                                action === "menu-close"
                              ) {
                                if (dropdownSearchTerm || pageNo > 1)
                                  handleDropdownActions(
                                    "stations",
                                    "searchIt",
                                    "",
                                  );
                                else setDropdownSearchTerm("");
                                setPageNo(1);
                              }
                            }}
                            onMenuScrollToBottom={() => {
                              if (!(stationsData?.pages[0]?.data as any).last)
                                handleDropdownActions("stations", "loadNext");
                            }}
                            page={pageNo}
                          />
                        )}
                      />
                    </Grid>
                    {/* ) : null} */}
                  </Grid>
                )}
                {loggedInUserData?.type === -1 ||
                loggedInUserData?.type === 100 ? null : (
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="stationIds"
                      control={control}
                      defaultValue={null}
                      rules={{ required: t("This field is required.") }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("station") + " *"}
                          defaultPlaceholder="phSelectStation"
                          {...field}
                          options={stations}
                          isClearable
                          isSearchable
                          onChange={(selectedOption: any) => {
                            field.onChange(selectedOption);
                          }}
                          error={errors.stationIds?.message as string}
                          isLoading={searchLoading === "stations"}
                          onInputChange={(
                            search: string,
                            { action }: { action: string },
                          ) => {
                            if (
                              action === "input-change" ||
                              (action !== "set-value" &&
                                action !== "menu-close" &&
                                action !== "input-blur")
                            )
                              handleDropdownActions(
                                "stations",
                                "searchIt",
                                search,
                              );
                            else if (
                              action === "input-blur" ||
                              action === "menu-close"
                            ) {
                              if (dropdownSearchTerm || pageNo > 1)
                                handleDropdownActions(
                                  "stations",
                                  "searchIt",
                                  "",
                                );
                              else setDropdownSearchTerm("");
                              setPageNo(1);
                            }
                          }}
                          onMenuScrollToBottom={() => {
                            if (!(stationsData?.pages[0]?.data as any).last)
                              handleDropdownActions("stations", "loadNext");
                          }}
                          page={pageNo}
                        />
                      )}
                    />
                  </Grid>
                )}
                {/* {watch("type")?.value === 150 ||
                (params?.id && watch("type") === 150) ? (*/}

                {/* ) : null} */}
                <Grid item xs={12}>
                  <h1
                    className="flex font-Saira font-[700] text-[17px]"
                    style={{ color: themeColors?.primary }}
                  >
                    {t("userDetails")}
                  </h1>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("name", {
                      validate: {
                        required: (value) =>
                          value.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={`${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                    placeholder={localizedUserFullNamePlaceholder(
                      primaryLanguage,
                    )}
                    title={`${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("secondaryName")}
                    label={`${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                    placeholder={localizedUserFullNamePlaceholder(
                      secondaryLanguage,
                    )}
                    title={`${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.secondaryName}
                    helperText={errors.secondaryName?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {watch("type")?.value === 150 || watch("type") === 150 ? (
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="expertise"
                      control={control}
                      defaultValue={null}
                      rules={{
                        validate: (value) => {
                          if (!isTechnicianType(watch("type"))) return true;
                          return value?.value
                            ? true
                            : t("This field is required.");
                        },
                      }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("expertise") + " *"}
                          {...field}
                          options={expertiseOptions}
                          isClearable
                          isSearchable
                          onChange={(selectedOption: any) => {
                            field.onChange(selectedOption);
                            if (!IsBtnEnabled) setIsBtnEnabled(true);
                          }}
                          error={errors.expertise?.message as string}
                        />
                      )}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={12} md={3}>
                  <TextField
                    type="email"
                    {...register("username", {
                      required: t("This field is required."),
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: t("validEmail"),
                      },
                    })}
                    label={t("email") + " *"}
                    placeholder={t("phUserEmail")}
                    title={t("email")}
                    fullWidth
                    error={!!errors.username}
                    helperText={errors.username?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {params?.id ? null : (
                  <>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type={watch("showPassword") ? "text" : "password"}
                        {...register("password", {
                          required: t("This field is required."),
                          validate: {
                            passwordLength: (value) =>
                              value.length >= 8 || t("minPasswordLength"),
                          },
                        })}
                        label={t("password") + " *"}
                        placeholder={t("phEnterPassword")}
                        title={t("password")}
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password?.message as string}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          endAdornment: watch("password") ? (
                            <InputAdornment
                              position="end"
                              className="cursor-pointer"
                            >
                              {watch("showPassword") ? (
                                <VisibilityIcon
                                  style={{ color: themeColors?.primary }}
                                  onClick={() =>
                                    setValue("showPassword", false)
                                  }
                                />
                              ) : (
                                <VisibilityOffIcon
                                  style={{ color: themeColors?.primary }}
                                  onClick={() => setValue("showPassword", true)}
                                />
                              )}
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type={
                          watch("showConfirmPassword") ? "text" : "password"
                        }
                        {...register("confirmPassword", {
                          required: t("This field is required."),
                          validate: {
                            passwordLength: (value) =>
                              value === watch("password") ||
                              t("confirmPasswordNewPassword"),
                          },
                        })}
                        label={t("confirmNewPassword") + " *"}
                        placeholder={t("phReenterPassword")}
                        title={t("confirmNewPassword")}
                        fullWidth
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message as string}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          endAdornment: watch("confirmPassword") ? (
                            <InputAdornment
                              position="end"
                              className="cursor-pointer"
                            >
                              {watch("showConfirmPassword") ? (
                                <VisibilityIcon
                                  style={{ color: themeColors?.primary }}
                                  onClick={() =>
                                    setValue("showConfirmPassword", false)
                                  }
                                />
                              ) : (
                                <VisibilityOffIcon
                                  style={{ color: themeColors?.primary }}
                                  onClick={() =>
                                    setValue("showConfirmPassword", true)
                                  }
                                />
                              )}
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Grid>
                  </>
                )}
                {watch("type")?.value === 130 || watch("type") === 130 ? (
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("batchNumber")}
                      label={t("badgeNumber")}
                      placeholder={t("phBadgeNumber")}
                      title={t("badgeNumber")}
                      fullWidth
                      error={!!errors.batchNumber}
                      helperText={errors.batchNumber?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={12} md={3}>
                  <TextField
                    type="text"
                    {...register("phone", {
                      validate: {
                        phoneLength: (value) =>
                          !value ||
                          (value.length >= 5 && value.length <= 17) ||
                          t("phoneLength"),
                      },
                    })}
                    label={t(phoneLabel)}
                    placeholder={t("phMobileNumber")}
                    title={t(phoneLabel)}
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone?.message as any}
                    InputLabelProps={{ shrink: true }}
                    onChange={(
                      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                    ) => {
                      const cleaned = e.target.value.replace(/[^0-9+]/g, "");
                      setValue("phone", cleaned);
                      if (!IsBtnEnabled) setIsBtnEnabled(true);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("address")}
                    label={t("address")}
                    placeholder={t("phUserAddress")}
                    title={t("address")}
                    fullWidth
                    error={!!errors.address}
                    helperText={errors.address?.message as string}
                    InputLabelProps={{ shrink: true }}
                    multiline
                    rows={1}
                    sx={{
                      "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                        width: "100%",
                        paddingRight: "0px",
                        paddingBottom: 0,
                      },
                      "& .MuiInputBase-inputMultiline": {
                        resize: "vertical",
                        minHeight: "29px",
                      },
                    }}
                  />
                </Grid>
                {watch("type")?.value === 110 || watch("type") === 110 ? (
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("taxNumber")}
                      label={t("taxNumber")}
                      placeholder={t("taxNumber")}
                      title={t("taxNumber")}
                      fullWidth
                      error={!!errors.taxNumber}
                      helperText={errors.taxNumber?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                ) : null}
                {watch("type")?.value === 150 || watch("type") === 150 ? (
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("employeeId", {
                        validate: {
                          required: (value) =>
                            (value && value?.trim() !== "") ||
                            t("This field is required."),
                        },
                      })}
                      label={t("employeeId") + " *"}
                      placeholder={t("employeeId")}
                      title={t("employeeId")}
                      fullWidth
                      error={!!errors.employeeId}
                      helperText={errors.employeeId?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                ) : null}
                {params?.id ? null : (
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="customRole"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <div style={{ paddingTop: "12px" }}>
                          <CommonCheckbox
                            {...field}
                            label={t("useCustomRole")}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              field.onChange(event.target.checked);
                              if (!event.target.checked) setValue("roleId", 0);
                            }}
                            checked={field.value}
                            color={themeColors?.primary}
                          />
                        </div>
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={3}>
                  <Controller
                    name="roleId"
                    control={control}
                    defaultValue={null}
                    rules={{
                      required: params?.id
                        ? t("This field is required.")
                        : watch("customRole")
                          ? t("This field is required.")
                          : false,
                    }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("role") + " *"}
                        defaultPlaceholder="phSelectRole"
                        {...field}
                        options={roles}
                        isSearchable
                        isDisabled={
                          params?.id
                            ? false
                            : watch("customRole")
                              ? false
                              : true
                        }
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.roleId?.message as string}
                        isLoading={searchLoading === "roles"}
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          )
                            handleDropdownActions("roles", "searchIt", search);
                          else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (dropdownSearchTerm || pageNo > 1)
                              handleDropdownActions("roles", "searchIt", "");
                            else setDropdownSearchTerm("");
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (!(rolesData?.pages[0]?.data as any).last)
                            handleDropdownActions("roles", "loadNext");
                        }}
                        page={pageNo}
                      />
                    )}
                  />
                </Grid>
                {/* {loggedInUserType === -1 || checkPrivileges('ROLE_LOGIN_AS') ? <Grid item container xs={12} spacing={3}>
                            <Grid item xs={12} md={5} className="flex items-center">
                                <TextField
                                    {...register("loginToken")}
                                    label={t('publicLoginLink')}
                                    placeholder={t('publicLoginLink')}
                                    title={t('publicLoginLink')}
                                    fullWidth
                                    error={!!errors.loginToken}
                                    helperText={errors.loginToken?.message as string}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (<InputAdornment position="end">
                                            <ReplayIcon onClick={() => {
                                                const token = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
                                                let newToken = '';
                                                for(let i = 0; i < 32; i++) {
                                                    newToken += token.charAt(Math.floor(Math.random() * token.length));
                                                }
                                                setValue('loginToken', newToken);
                                                if(!IsBtnEnabled) setIsBtnEnabled(true);
                                            }} sx={{ cursor: 'pointer', marginRight: '5px' }} />
                                        </InputAdornment>)
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { paddingRight: '0px !important' } }}
                                />
                                {watch('loginToken') ? <span onClick={() => {setValue('loginToken', ''); if(!IsBtnEnabled) setIsBtnEnabled(true);}}
                                    className="underline font-Saira ms-[10px] italic text-[13px] font-semibold cursor-pointer"
                                    style={{ color: themeColors?.primary }}
                                >{t('Clear')}</span> : null}
                            </Grid>
                        </Grid> : null} */}
                {watch("type")?.value === 110 ||
                watch("type") === 110 ||
                watch("type")?.value === 100 ||
                watch("type") === 100 ? (
                  <Grid item container xs={12} spacing={3}>
                    <Grid item xs={12}>
                      <h1
                        className="flex font-Saira font-[700] text-[17px]"
                        style={{ color: themeColors?.primary }}
                      >
                        {t("business") + " " + t("Details")}
                      </h1>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("business.title", {
                          validate: {
                            required: (value) =>
                              value?.trim() !== "" ||
                              t("This field is required."),
                          },
                        })}
                        label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${primaryLanguage})` : ""} *`}
                        placeholder={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${primaryLanguage})` : ""} *`
                          .replace(/\s+/g, " ")
                          .trim()}
                        title={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${primaryLanguage})` : ""} *`
                          .replace(/\s+/g, " ")
                          .trim()}
                        fullWidth
                        error={!!(errors.business as any)?.title}
                        helperText={
                          (errors.business as any)?.title?.message as string
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("business.secondaryTitle")}
                        label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${secondaryLanguage})` : ""}`}
                        placeholder={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${secondaryLanguage})` : ""}`
                          .replace(/\s+/g, " ")
                          .trim()}
                        title={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${secondaryLanguage})` : ""}`
                          .replace(/\s+/g, " ")
                          .trim()}
                        fullWidth
                        error={!!(errors.business as any)?.secondaryTitle}
                        helperText={
                          (errors.business as any)?.secondaryTitle
                            ?.message as string
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="business.primaryLanguage"
                        control={control}
                        defaultValue={null}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("primaryLanguage")}
                            {...field}
                            options={language.map(
                              (item: { value: string; title: string }) => ({
                                value: item.value,
                                label: item.title,
                              }),
                            )}
                            isSearchable
                            isClearable
                            onChange={(selectedOption: any) =>
                              field.onChange(selectedOption)
                            }
                            error={
                              (errors.business as any)?.primaryLanguage
                                ?.message as string
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="business.secondaryLanguage"
                        control={control}
                        defaultValue={null}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("secondaryLanguage")}
                            {...field}
                            options={language.map(
                              (item: { value: string; title: string }) => ({
                                value: item.value,
                                label: item.title,
                              }),
                            )}
                            isSearchable
                            isClearable
                            onChange={(selectedOption: any) =>
                              field.onChange(selectedOption)
                            }
                            error={
                              (errors.business as any)?.secondaryLanguage
                                ?.message as string
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="business.currency"
                        control={control}
                        defaultValue={null}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("currency")}
                            {...field}
                            options={currency.map(
                              (item: { value: string; title: string }) => ({
                                value: item.value,
                                label: item.title,
                              }),
                            )}
                            isSearchable
                            isClearable
                            onChange={(selectedOption: any) =>
                              field.onChange(selectedOption)
                            }
                            error={
                              (errors.business as any)?.currency
                                ?.message as string
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                ) : null}
              </Grid>
            }
          />
        </div>
      )}
    </>
  );
}
